import type { AnalysisResult } from "@/lib/analysis.functions";

const eur = (n: number) =>
  new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);
const num = (n: number) => new Intl.NumberFormat("de-DE").format(n || 0);

export function AnalysisReport({ data, onClose }: { data: AnalysisResult; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm overflow-y-auto" onClick={onClose}>
      <div
        className="min-h-full flex items-start justify-center p-4 md:p-10"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="w-full max-w-4xl bg-[var(--paper)] border border-[var(--rule)] shadow-2xl">
          <div className="flex items-center justify-between px-6 md:px-10 py-5 border-b border-[var(--rule)] bg-[var(--leaf-deep)] text-[var(--paper)] sticky top-0">
            <div>
              <div className="eyebrow opacity-80">Ihre KI-Analyse</div>
              <h3 className="font-display text-xl md:text-2xl">Projektübersicht & Angebot</h3>
            </div>
            <button onClick={onClose} className="text-2xl leading-none hover:opacity-70" aria-label="Schließen">×</button>
          </div>

          <div className="p-6 md:p-10 space-y-10 text-sm text-[var(--ink)]">
            {/* Standort */}
            <section>
              <h4 className="font-display text-lg text-[var(--leaf-deep)] mb-3">Standort & Sonnenpotenzial</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Stat label="Adresse" value={data.standort.adresse} />
                <Stat label="Region" value={data.standort.region} />
                <Stat label="Sonnenstunden / Jahr" value={`${num(data.standort.sonnenstundenJahr)} h`} />
                <Stat label="Globalstrahlung" value={`${num(data.standort.globalstrahlungKwhM2)} kWh/m²`} />
              </div>
              <p className="mt-3 text-muted-foreground">{data.standort.bemerkungStandort}</p>
            </section>

            {/* PV */}
            <section>
              <h4 className="font-display text-lg text-[var(--leaf-deep)] mb-3">Photovoltaik & Speicher</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Stat label="Anlagenleistung" value={`${num(data.photovoltaik.empfohleneKwp)} kWp`} />
                <Stat label="Module" value={`${num(data.photovoltaik.anzahlModule)} × ${num(data.photovoltaik.moduleLeistungWp)} Wp`} />
                <Stat label="Erzeugung / Jahr" value={`${num(data.photovoltaik.erwarteteJahreserzeugungKwh)} kWh`} />
                <Stat label="Speicher" value={`${num(data.photovoltaik.speicherEmpfohlenKwh)} kWh`} />
              </div>
              <Money label="Investition (Obergrenze)" value={data.photovoltaik.investitionMaxEur} />
              <p className="mt-2 text-muted-foreground">{data.photovoltaik.begruendung}</p>
            </section>

            {/* Wärmepumpe */}
            <section>
              <h4 className="font-display text-lg text-[var(--leaf-deep)] mb-3">Wärmepumpe</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Stat label="Heizlast" value={`${num(data.waermepumpe.heizlastKw)} kW`} />
                <Stat label="Anzahl Geräte" value={num(data.waermepumpe.anzahl)} />
                <Stat label="Pro Gerät" value={`${num(data.waermepumpe.leistungProGeraetKw)} kW`} />
                <Stat label="Typ" value={data.waermepumpe.typ} />
              </div>
              <Money label="Investition (Obergrenze)" value={data.waermepumpe.investitionMaxEur} />
              <p className="mt-2 text-muted-foreground">{data.waermepumpe.begruendung}</p>
            </section>

            {/* Sanierung */}
            {data.sanierung.length > 0 && (
              <section>
                <h4 className="font-display text-lg text-[var(--leaf-deep)] mb-3">Sanierungsmaßnahmen</h4>
                <table className="w-full text-left border-collapse">
                  <thead className="text-xs uppercase tracking-wider text-muted-foreground">
                    <tr>
                      <th className="py-2 border-b border-[var(--rule)]">Maßnahme</th>
                      <th className="py-2 border-b border-[var(--rule)] text-right">Investition max.</th>
                      <th className="py-2 border-b border-[var(--rule)] text-right">Einsparung</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.sanierung.map((m, i) => (
                      <tr key={i} className="border-b border-[var(--rule)]/50">
                        <td className="py-2 pr-3">{m.massnahme}</td>
                        <td className="py-2 text-right">{eur(m.investitionMaxEur)}</td>
                        <td className="py-2 text-right">{num(m.energieeinsparungProzent)} %</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            )}

            {/* Förderung */}
            <section>
              <h4 className="font-display text-lg text-[var(--leaf-deep)] mb-3">Fördermittel & Programme</h4>
              <div className="space-y-3">
                {data.foerderung.map((f, i) => (
                  <div key={i} className="border border-[var(--rule)] surface-cloud p-4">
                    <div className="flex items-baseline justify-between gap-3 flex-wrap">
                      <div className="font-medium text-[var(--ink)]">{f.programm} <span className="text-muted-foreground font-normal">— {f.traeger}</span></div>
                      <div className="text-[var(--leaf-deep)] font-medium">bis {eur(f.maxFoerderungEur)}</div>
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">{f.bezug}</div>
                    <div className="mt-2 flex gap-4 text-xs">
                      <span>Tilgungszuschuss: <strong>{num(f.tilgungszuschussProzent)} %</strong></span>
                      <span>Zins: <strong>{num(f.zinssatzProzent)} %</strong></span>
                    </div>
                    {f.hinweis && <p className="mt-2 text-muted-foreground">{f.hinweis}</p>}
                  </div>
                ))}
              </div>
            </section>

            {/* Steuer */}
            <section>
              <h4 className="font-display text-lg text-[var(--leaf-deep)] mb-3">Steuerliche Effekte</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Stat label="AfA-Satz" value={`${num(data.steuer.afaProzentJahr)} % / Jahr`} />
                <Stat label="AfA / Jahr" value={eur(data.steuer.afaBetragJahrEur)} />
                <Stat label="EAB sinnvoll" value={data.steuer.eabSinnvoll ? "Ja" : "Nein"} />
                <Stat label="Steuerersparnis / Jahr" value={eur(data.steuer.steuerersparnisJahrEur)} />
              </div>
              <p className="mt-2 text-muted-foreground">{data.steuer.eabBegruendung}</p>
            </section>

            {/* Vorher/Nachher */}
            <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border border-[var(--rule)] p-5 surface-sand-soft">
                <div className="eyebrow text-[var(--ink)]/60 mb-3">Heute</div>
                <Money label="Strom / Jahr" value={data.istSituation.stromkostenJahrEur} />
                <Money label="Heizung / Jahr" value={data.istSituation.heizkostenJahrEur} />
                <Money label="Gesamt / Jahr" value={data.istSituation.gesamtJahrEur} bold />
              </div>
              <div className="border border-[var(--leaf)] p-5 surface-cloud">
                <div className="eyebrow text-[var(--leaf-deep)] mb-3">Nach Umsetzung</div>
                <Money label="Strom / Jahr" value={data.zukunftSituation.stromkostenJahrEur} />
                <Money label="Heizung / Jahr" value={data.zukunftSituation.heizkostenJahrEur} />
                <Money label="Zusatzeinnahmen / Jahr" value={data.zukunftSituation.zusatzeinnahmenJahrEur} />
                <Money label="Saldo / Jahr" value={data.zukunftSituation.gesamtSaldoJahrEur} bold />
                <p className="mt-2 text-xs text-muted-foreground">{data.zukunftSituation.zusatzeinnahmenBegruendung}</p>
              </div>
            </section>

            {/* Investition */}
            <section className="border border-[var(--leaf-deep)] p-5 surface-sand">
              <h4 className="font-display text-lg text-[var(--leaf-deep)] mb-3">Investition gesamt</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Stat label="Brutto" value={eur(data.investitionGesamt.bruttoEur)} />
                <Stat label="Förderung" value={`− ${eur(data.investitionGesamt.foerderungAbzugEur)}`} />
                <Stat label="Netto" value={eur(data.investitionGesamt.nettoEur)} />
                <Stat label="Amortisation" value={`${num(data.investitionGesamt.amortisationJahre)} Jahre`} />
              </div>
              <p className="mt-3 text-muted-foreground">{data.investitionGesamt.finanzierungVorschlag}</p>
            </section>

            {/* Empfehlung */}
            <section className="border-l-4 border-[var(--leaf)] pl-4">
              <h4 className="font-display text-lg text-[var(--leaf-deep)] mb-2">Empfehlung</h4>
              <p className="leading-relaxed">{data.empfehlung}</p>
            </section>

            {data.hinweise.length > 0 && (
              <section className="text-xs text-muted-foreground">
                <div className="eyebrow mb-2">Hinweise & Annahmen</div>
                <ul className="list-disc pl-5 space-y-1">
                  {data.hinweise.map((h, i) => <li key={i}>{h}</li>)}
                </ul>
              </section>
            )}

            <div className="flex justify-end pt-4 border-t border-[var(--rule)]">
              <button onClick={onClose} className="bg-[var(--ink)] text-[var(--paper)] px-5 py-3 text-sm hover:bg-[var(--leaf)] transition">
                Schließen
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div>
      <div className="eyebrow text-[var(--ink)]/60">{label}</div>
      <div className="font-display text-base md:text-lg text-[var(--ink)] mt-1">{value}</div>
    </div>
  );
}

function Money({ label, value, bold = false }: { label: string; value: number; bold?: boolean }) {
  return (
    <div className={`flex items-baseline justify-between gap-3 ${bold ? "border-t border-[var(--rule)] mt-2 pt-2" : "mt-1"}`}>
      <span className="text-muted-foreground">{label}</span>
      <span className={bold ? "font-display text-lg text-[var(--leaf-deep)]" : ""}>{eur(value)}</span>
    </div>
  );
}
